/**
 * レイヤー素材のレイヤーを確認・編集するモーダル (motion-graphics-mode-plan.md §5.2・利用者の指示 2026-09-30
 * 「選択済み素材パネルの中にレイヤー一覧を出すと複雑なので、モーダルとしてレイヤーを出してほしい」)。
 *
 * 中身は MotionLayerList (合成プレビュー 最大 60vh・レイヤー行・固定の説明・warnings)。名前と固定は
 * layerEditsStore (sessionStorage) に入るので、閉じても残る。構造は既存モーダル (ScenarioInfoModal 等) の
 * modal-overlay / modal-card / modal-header / modal-body / modal-actions に倣う。
 */
import { useEffect, useId } from 'react';
import { createPortal } from 'react-dom';
import type { GenerationAsset } from '../state/types';
import { layerMaterialDisplayName, type LayerStack } from '../../../features/motion/layerStack';
import { MotionLayerList } from './MotionLayerList';
import styles from '../scenario/ScenarioGenerator.module.css';

type Props = {
  asset: GenerationAsset;
  stack: LayerStack;
  onClose: () => void;
};

export function MotionLayerModal({ asset, stack, onClose }: Props) {
  const titleId = useId();

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      // 名前の編集中の Esc は入力の取り消し (MotionLayerList) に任せ、モーダルは閉じない
      const target = e.target as HTMLElement | null;
      if (target && target.tagName === 'INPUT' && (target as HTMLInputElement).type === 'text') return;
      onClose();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [onClose]);

  return createPortal(
    <div
      className="modal-overlay"
      data-testid="motion-layer-modal-backdrop"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        className={`modal-card ${styles.motionLayerModalCard}`}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        data-testid="motion-layer-modal"
      >
        <div className="modal-header">
          <h3 className="modal-title" id={titleId}>
            {`レイヤー (${stack.layers.length}) — ${layerMaterialDisplayName(asset, stack)}`}
          </h3>
          <button type="button" className="modal-close-button" onClick={onClose} aria-label="閉じる">
            ✕
          </button>
        </div>
        <div className="modal-body">
          <MotionLayerList asset={asset} stack={stack} previewMaxHeight="60vh" />
        </div>
        <div className="modal-actions">
          <button type="button" className="btn-modal-secondary" onClick={onClose}>
            閉じる
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
